import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { PracticeCallResponse } from '../types';

export interface StartPracticeCallParams {
	companyId: string;
	leadServedId: string;
	contactId: string;
}

/**
 * Hook for starting an OpenAI practice call session
 */
export function useStartOpenAIPracticeCall() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({
			companyId,
			leadServedId,
			contactId,
		}: StartPracticeCallParams): Promise<PracticeCallResponse> => {
			if (!companyId) {
				throw new Error('Company ID is required');
			}

			const response = await userSession.webServiceHelper.callWebServiceAsync<PracticeCallResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/startPracticeCall/openai?leadServedId=${leadServedId}&contactId=${contactId}`,
				}),
				'POST',
				''
			);

			if (!response.value) {
				throw new Error('No data returned from OpenAI practice call API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to start OpenAI practice call:', error);
		},
	});
}

/**
 * Hook for starting an ElevenLabs practice call session
 */
export function useStartElevenLabsPracticeCall() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({
			companyId,
			leadServedId,
			contactId,
		}: StartPracticeCallParams): Promise<PracticeCallResponse> => {
			if (!companyId) {
				throw new Error('Company ID is required');
			}

			const response = await userSession.webServiceHelper.callWebServiceAsync<PracticeCallResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/startPracticeCall/elevenLabs?leadServedId=${leadServedId}&contactId=${contactId}`,
				}),
				'POST',
				''
			);

			if (!response.value) {
				throw new Error('No data returned from ElevenLabs practice call API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to start ElevenLabs practice call:', error);
		},
	});
}

/**
 * Combined hook that provides both mutations
 */
export function usePracticeCallMutations() {
	const openAIMutation = useStartOpenAIPracticeCall();
	const elevenLabsMutation = useStartElevenLabsPracticeCall();

	return {
		openAI: openAIMutation,
		elevenLabs: elevenLabsMutation,
	};
}
